import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, IconButton } from '@mui/material';

const GlobalTour = ({ show, steps, stepIndex, onClose, onNext, t }) => {
  const [, forceRefresh] = useState(0);

  const step = show ? steps[stepIndex] : null;
  const element = show && step?.target ? document.querySelector(step.target) : null;

  useLayoutEffect(() => {
    if (!show || !element) return undefined;

    let animationFrame;
    const refresh = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => forceRefresh(prev => prev + 1));
    };

    window.addEventListener('scroll', refresh, true);
    window.addEventListener('resize', refresh);

    return () => {
      window.removeEventListener('scroll', refresh, true);
      window.removeEventListener('resize', refresh);
      cancelAnimationFrame(animationFrame);
    };
  }, [element, forceRefresh, show]);

  if (!show) return null;

  if (!element) {
    onNext();
    return null;
  }

  const rect = element.getBoundingClientRect();
  const { top, left, width, height, bottom } = rect;
  const highlightPadding = 8;
  const messageOffset = 12;

  const screenHeight = window.innerHeight;
  const spaceBelow = screenHeight - bottom;
  const showAbove = spaceBelow < 160;
  const messageBaseTop = showAbove ? top - messageOffset : bottom + messageOffset;
  const messageTransform = showAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.4)',
          zIndex: 1300,
        }}
        onClick={onClose}
      />

      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1350,
          pointerEvents: 'none',
        }}>
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            top: top - highlightPadding,
            left: left - highlightPadding,
            width: width + highlightPadding * 2,
            height: height + highlightPadding * 2,
            border: theme => `4px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
            transition: 'all 0.2s ease',
          }}
        />

        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: messageBaseTop,
            left: left + width / 2,
            transform: messageTransform,
            zIndex: 1400,
            maxWidth: 420,
            p: 3,
            borderRadius: 2,
            pointerEvents: 'auto',
          }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {step?.message}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {stepIndex + 1} / {steps.length}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={onClose}>
                {t('demoTour.close')}
              </IconButton>
              <IconButton
                size="small"
                onClick={onNext}
                sx={theme => ({
                  borderRadius: '8px',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': { bgcolor: theme.palette.primary.light },
                })}>
                {stepIndex === steps.length - 1 ? t('demoTour.done') : t('demoTour.next')}
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

GlobalTour.propTypes = {
  show: PropTypes.bool,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      message: PropTypes.node,
    }),
  ),
  stepIndex: PropTypes.number,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default GlobalTour;
